/**
 * UTM parameter capture and retrieval.
 *
 * On landing page load, call captureUtmParams() to snapshot any UTM query
 * parameters into sessionStorage. Later, call getUtmParams() (e.g. on signup
 * or PostHog identify) to retrieve them.
 */

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

const STORAGE_KEY = 'myl_utm';

export type UtmParams = Partial<Record<(typeof UTM_KEYS)[number], string>>;

/**
 * Read UTM parameters from the current URL and persist them in
 * sessionStorage. Only writes if at least one UTM param is present and no
 * previous capture exists for this session (first-touch attribution).
 */
export const captureUtmParams = (): void => {
  // Don't overwrite — keep the first-touch values for the session
  if (sessionStorage.getItem(STORAGE_KEY)) return;

  const params = new URLSearchParams(window.location.search);
  const utm: UtmParams = {};

  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value) utm[key] = value;
  }

  if (Object.keys(utm).length > 0) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(utm));
  }
};

/**
 * Retrieve previously captured UTM params (or an empty object).
 */
export const getUtmParams = (): UtmParams => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};
